import { gql } from '@apollo/client';
import { DocumentNode } from 'graphql';

import { TLatestRequest } from 'Components/LatestRequests/LatestRequests.helpers';
import analytics from 'utils/analytics/analytics';

type LatestRequestsData = {
  id: string;
  requests: TLatestRequest[];
};

export type LatestRequestsResponse =
  | {
      l2BySlug: LatestRequestsData;
    }
  | {
      l3BySlug: LatestRequestsData;
    }
  | {
      providerLandingPageDataByL3City: LatestRequestsData;
    }
  | {
      providerLandingPageDataByCity: LatestRequestsData;
    };

const latestRequestFragment = gql`
  fragment LatestRequest on CategoryRequest {
    id
    user {
      id
      name
      gender
      avatar
    }
    city {
      name
      regionName
    }
    l4Category {
      name
      slug
    }
    providers {
      id
      avatar
    }
    details {
      question_id
      question
      answer_id
      answer
    }
  }
`;

export const latestRequestsByL2Query = gql`
  ${latestRequestFragment}
  query LatestRequestsByL2($slug: String!) {
    l2BySlug(slug: $slug, city: "warszawa") {
      id
      requests {
        ...LatestRequest
      }
    }
  }
`;

export const latestRequestsByL3Query = gql`
  ${latestRequestFragment}
  query LatestRequestsByL3($slug: String!) {
    l3BySlug(slug: $slug) {
      id
      requests {
        ...LatestRequest
      }
    }
  }
`;

export const latestRequestsByL3CityQuery = gql`
  ${latestRequestFragment}
  query LatestRequestsByL3City($slug: String!, $city: String!) {
    providerLandingPageDataByL3City(l3Slug: $slug, citySlug: $city) {
      id
      requests {
        ...LatestRequest
      }
    }
  }
`;

export const latestRequestsByCityQuery = gql`
  ${latestRequestFragment}
  query LatestRequestsByCity($city: String!) {
    providerLandingPageDataByCity(slug: $city) {
      id
      requests {
        ...LatestRequest
      }
    }
  }
`;

type LatestRequestOptions = {
  l2Slug?: string;
  l3Slug?: string;
  citySlug?: string;
};

export const selectQuery = (options: LatestRequestOptions): DocumentNode => {
  const { l2Slug, l3Slug, citySlug } = options;
  if (l3Slug && citySlug) return latestRequestsByL3CityQuery;
  if (citySlug) return latestRequestsByCityQuery;
  if (l2Slug) return latestRequestsByL2Query;
  return latestRequestsByL3Query;
};

export const selectDataSource = (data: LatestRequestsResponse): TLatestRequest[] => {
  if ('providerLandingPageDataByL3City' in data) return data.providerLandingPageDataByL3City.requests;
  if ('l2BySlug' in data) return data.l2BySlug.requests;
  if ('providerLandingPageDataByCity' in data) {
    return data.providerLandingPageDataByCity.requests;
  } else {
    return data.l3BySlug.requests;
  }
};

export const trackDetailsClick = (options: LatestRequestOptions): void => {
  const { l2Slug, l3Slug, citySlug } = options;
  let entityId: string;
  if (l3Slug && citySlug) {
    entityId = `${l3Slug}/${citySlug}`;
  } else {
    entityId = String(l2Slug || l3Slug || citySlug);
  }

  analytics.trackEvent('sp_registration_request_click', { rel_entity_id: entityId });
};
